'use client';

import { useEffect } from 'react';

import { analytics } from '@kit/analytics';

import {
  isLegacyOrigin,
  LEGACY_ORIGIN_COOKIE,
  type LegacyOrigin,
} from '~/lib/legacy-origin';

const SESSION_FLAG_KEY = 'legacy_origin_event_fired';
const COOKIE_PREFIX = `${LEGACY_ORIGIN_COOKIE}=`;

function readLegacyOriginCookie(): LegacyOrigin | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(COOKIE_PREFIX));
  if (!match) return null;
  const value = match.slice(COOKIE_PREFIX.length);
  return isLegacyOrigin(value) ? value : null;
}

/**
 * Fires `legacy_user_signin_attempt` once per browser session when a
 * user lands on an auth page carrying the `legacy_origin` cookie set by
 * the proxy handoff (see apps/web/proxy.ts → `handleLegacyOriginHandoff`).
 *
 * Pairs with the post-sign-in identify call elsewhere in the app —
 * PostHog buffers anonymous-event properties under `person_profiles:
 * 'identified_only'` so the `legacy_origin` event property carries
 * forward to the user's profile when they identify.
 */
export function LegacyOriginCapture() {
  useEffect(() => {
    const origin = readLegacyOriginCookie();
    if (!origin) return;

    if (typeof window !== 'undefined') {
      try {
        if (window.sessionStorage.getItem(SESSION_FLAG_KEY)) return;
        window.sessionStorage.setItem(SESSION_FLAG_KEY, '1');
      } catch {
        // sessionStorage can throw in private-mode browsers — degrade to
        // firing on every mount rather than failing silently.
      }
    }

    void analytics.trackEvent('legacy_user_signin_attempt', {
      legacy_origin: origin,
    });
  }, []);

  return null;
}
